/**
 * OntologyExplorer — unified Decision Ontology viewer for any client.
 *
 * Route: /admin/ontology/:clientUid/explorer
 *        /admin/ontology/:clientUid/explorer/:version
 *
 * Shows a maturity badge derived from the loaded entity types:
 *   - "V8 PDO · Curated"  when the graph has typed PDO concepts (Attribute/Need/Value/…)
 *   - "Pipeline · Day 1"  when the graph has cognitive-pipeline entities (buyer_scenario/…)
 *
 * Layout: clean dark card that fills the viewport height — no negative-margin
 * breakout hack. Fullscreen mode still available via the toolbar button.
 */
import { useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  GitBranch,
  Network,
  BookOpen,
  Loader2,
  AlertCircle,
  Maximize2,
  Minimize2,
  Sparkles,
  Layers,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useOntologyStore, type OntologyTab } from '@/stores/ontology';
import { type OntologyVersionInfo } from '@/services/api';
import { ExplorerTab } from '@/components/ontology/ExplorerTab';
import { EvolutionTab } from '@/components/ontology/EvolutionTab';
import { ReferenceTab } from '@/components/ontology/ReferenceTab';
import { useResolvedClientUid } from '@/components/pdo/utils';

const TABS: { id: OntologyTab; label: string; icon: React.ElementType }[] = [
  { id: 'explorer',  label: 'Explorer',      icon: Network   },
  { id: 'evolution', label: 'Evolution',      icon: GitBranch },
  { id: 'reference', label: 'Reference Docs', icon: BookOpen  },
];

// ---------------------------------------------------------------------------
// Maturity derivation
// ---------------------------------------------------------------------------

const PDO_V8_TYPES = new Set([
  'attribute', 'need', 'value', 'trust', 'barrier', 'behavior', 'category', 'product',
]);

type Maturity = 'mature' | 'pipeline' | null;

function deriveMaturity(versions: OntologyVersionInfo[]): Maturity {
  if (!versions.length) return null;
  const latest = versions[versions.length - 1];
  const types = (latest.entity_types ?? []).map((t) => t.toLowerCase());
  return types.some((t) => PDO_V8_TYPES.has(t)) ? 'mature' : 'pipeline';
}

function MaturityBadge({ maturity }: { maturity: Maturity }) {
  if (!maturity) return null;

  if (maturity === 'mature') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold border bg-emerald-500/10 text-emerald-400 border-emerald-500/25">
        <Sparkles size={9} />
        V8 PDO · Curated
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold border bg-amber-500/10 text-amber-400 border-amber-500/25">
      <Layers size={9} />
      Pipeline · Day 1
    </span>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function ExplorerHeader({
  clientUid,
  fullscreen,
  onToggleFullscreen,
}: {
  clientUid: string | null;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}) {
  const selectedVersion    = useOntologyStore((s) => s.selectedVersion);
  const selectedClient     = useOntologyStore((s) => s.selectedClient);
  const availableClients   = useOntologyStore((s) => s.availableClients);
  const activeTab          = useOntologyStore((s) => s.activeTab);
  const versions           = useOntologyStore((s) => s.versions);
  const setTab             = useOntologyStore((s) => s.setTab);
  const setVersion         = useOntologyStore((s) => s.setVersion);
  const setClient          = useOntologyStore((s) => s.setClient);

  const currentVersionInfo = versions.find((v) => v.version === selectedVersion);
  const maturity           = deriveMaturity(versions);

  return (
    <div className="flex-shrink-0 px-6 pt-5 pb-0 bg-navy-950 border-b border-navy-800 rounded-t-xl">
      <div className="flex items-center justify-between mb-4">
        {/* Title + client */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow flex-shrink-0">
            <Network size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-white leading-tight">
                Ontology Explorer
              </h1>
              <MaturityBadge maturity={maturity} />
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Purchase Decision Ontology visualisation
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Client selector */}
          {availableClients.length > 1 && (
            <select
              value={selectedClient}
              onChange={(e) => setClient(e.target.value)}
              className="px-3 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:border-brand-teal"
            >
              {availableClients.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}

          {/* Version selector */}
          {versions.length > 0 && (
            <select
              value={selectedVersion ?? ''}
              onChange={(e) => setVersion(e.target.value)}
              className="px-3 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:border-brand-teal"
            >
              {versions.map((v) => (
                <option key={v.version} value={v.version}>
                  {v.version}
                  {v.concept_count != null ? ` — ${v.concept_count} concepts` : ''}
                </option>
              ))}
            </select>
          )}

          {/* Stats chip */}
          {currentVersionInfo && (
            <div className="flex items-center gap-3 px-3 py-1.5 bg-navy-800 border border-navy-700 rounded-lg">
              <span className="text-[11px] font-mono text-slate-400">
                <span className="text-brand-teal font-bold">
                  {currentVersionInfo.concept_count}
                </span>{' '}
                concepts
              </span>
              <span className="text-navy-600">·</span>
              <span className="text-[11px] font-mono text-slate-400">
                <span className="text-brand-teal font-bold">
                  {currentVersionInfo.edge_count}
                </span>{' '}
                relations
              </span>
            </div>
          )}

          {/* Fullscreen toggle */}
          <button
            onClick={onToggleFullscreen}
            title={fullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-navy-700 transition-colors"
          >
            {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1">
        {TABS.map((tab) => {
          const Icon   = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative',
                active
                  ? 'text-white bg-navy-900 border-t border-l border-r border-navy-700 border-b border-b-navy-900 -mb-px'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-navy-900/50',
              )}
            >
              <Icon size={15} className={active ? 'text-brand-teal' : ''} />
              {tab.label}
              {active && (
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-brand-teal rounded-t-lg" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab content
// ---------------------------------------------------------------------------

function TabContent({ clientUid }: { clientUid: string | null }) {
  const versionsLoading = useOntologyStore((s) => s.versionsLoading);
  const versionsError   = useOntologyStore((s) => s.versionsError);
  const activeTab       = useOntologyStore((s) => s.activeTab);
  const fetchVersions   = useOntologyStore((s) => s.fetchVersions);

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden bg-navy-950 relative rounded-b-xl">
      {versionsLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-navy-950/80 rounded-b-xl">
          <div className="text-center">
            <Loader2 size={28} className="animate-spin text-brand-teal mx-auto mb-3" />
            <p className="text-sm text-slate-400">Loading ontology graph…</p>
          </div>
        </div>
      )}

      {versionsError && !versionsLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 rounded-b-xl">
          <div className="text-center max-w-md">
            <AlertCircle size={28} className="text-red-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-white mb-1">
              Failed to load ontology
            </p>
            <p className="text-xs text-slate-400 mb-4">{versionsError}</p>
            <button
              onClick={() => clientUid && fetchVersions(clientUid)}
              className="px-4 py-2 bg-brand-teal text-navy-900 rounded-lg text-sm font-medium hover:bg-brand-teal/90 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!versionsLoading && !versionsError && activeTab === 'explorer'  && <ExplorerTab />}
      {!versionsLoading && !versionsError && activeTab === 'evolution' && <EvolutionTab />}
      {!versionsLoading && !versionsError && activeTab === 'reference' && <ReferenceTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function OntologyExplorer() {
  const { version: versionParam } = useParams<{
    clientUid?: string;
    version?: string;
  }>();

  const clientUid = useResolvedClientUid('/admin/ontology');

  const selectedVersion   = useOntologyStore((s) => s.selectedVersion);
  const selectedClient    = useOntologyStore((s) => s.selectedClient);
  const availableClients  = useOntologyStore((s) => s.availableClients);
  const versions          = useOntologyStore((s) => s.versions);
  const versionsLoading   = useOntologyStore((s) => s.versionsLoading);
  const fullscreen        = useOntologyStore((s) => s.fullscreen);

  const setClient       = useOntologyStore((s) => s.setClient);
  const setVersion      = useOntologyStore((s) => s.setVersion);
  const fetchVersions   = useOntologyStore((s) => s.fetchVersions);
  const fetchClients    = useOntologyStore((s) => s.fetchClients);
  const setFullscreen   = useOntologyStore((s) => s.setFullscreen);

  const toggleFullscreen = useCallback(
    () => setFullscreen(!fullscreen),
    [fullscreen, setFullscreen],
  );

  // On first mount: populate the client list from the API so the selector
  // reflects whatever is registered in GCS rather than a hardcoded list.
  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientUid) return;
    if (clientUid !== selectedClient) {
      setClient(clientUid);
    } else if (versions.length === 0 && !versionsLoading) {
      fetchVersions(clientUid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid]);

  useEffect(() => {
    if (versionParam && versions.length > 0 && versionParam !== selectedVersion) {
      setVersion(versionParam);
    }
  }, [versionParam, versions, selectedVersion, setVersion]);

  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fullscreen, setFullscreen]);

  // Fullscreen: still edge-to-edge
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-navy-950">
        <ExplorerHeader
          clientUid={clientUid}
          fullscreen={true}
          onToggleFullscreen={toggleFullscreen}
        />
        <TabContent clientUid={clientUid} />
        <div className="absolute top-3 right-16 z-50 pointer-events-none">
          <span className="text-[10px] text-slate-600 font-mono bg-navy-900/80 px-2 py-0.5 rounded border border-navy-700">
            ESC to exit
          </span>
        </div>
      </div>
    );
  }

  // Default: clean dark card that fills the available viewport height
  return (
    <div
      className={clsx(
        'flex flex-col rounded-xl border border-navy-800 shadow-2xl overflow-hidden',
        'bg-navy-950',
        // Fill viewport minus topbar (4rem / 64px) and page padding (p-6 = 1.5rem top)
        'h-[calc(100vh-7rem)]',
      )}
    >
      <ExplorerHeader
        clientUid={clientUid}
        fullscreen={false}
        onToggleFullscreen={toggleFullscreen}
      />
      <TabContent clientUid={clientUid} />
    </div>
  );
}
